import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export type OpcaoTypeahead = {
  id: string;
  rotulo: string;
  /** texto de busca (codigo + nome). */
  busca: string;
  /** Opcional: usado em listas de produtos/serviços. */
  precoUnitario?: number;
  estoqueQuantidade?: number;
  desabilitada?: boolean;
  motivoDesabilitado?: string;
  /** Opcional: campos separados (peça com dois campos código | nome). */
  codigo?: string;
  nome?: string;
};

export type ItemTypeaheadValor =
  | { tipo: 'existente'; opcao: OpcaoTypeahead }
  // `codigo` acompanha o item novo quando a peça é cadastrada por dois
  // campos (código + nome) — preserva o código digitado para criar o produto.
  | { tipo: 'novo'; descricao: string; codigo?: string };

type Props = {
  opcoes: OpcaoTypeahead[];
  /** valor atual: existente (id) ou novo (texto livre). */
  valor: ItemTypeaheadValor | null;
  onChange: (valor: ItemTypeaheadValor | null) => void;
  ariaLabel: string;
  placeholder?: string;
  mensagemVazio?: string;
  onConfirmarNovo?: (descricao: string) => void | Promise<void>;
};

type Posicao = { top: number; left: number; width: number; abrirParaCima: boolean };

function normalizar(s: string): string {
  return s.toLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, '');
}

/**
 * Quantidade máxima de itens que o dropdown renderiza de uma vez. Listas
 * grandes (5k+ produtos) travavam a UI ao abrir o dropdown sem texto
 * digitado — o React montava 5k <li> síncronos, gerando freeze de 1-2s.
 * Acima desse cap o usuário vê uma linha "refine a busca"; o filtro
 * continua varrendo o array completo, então qualquer letra digitada
 * estreita o resultado abaixo do cap.
 */
const TAMANHO_MAX_VISIVEL = 100;

/**
 * Combobox com modo "chip": quando há valor selecionado e o input não está
 * em foco, mostra a seleção como um badge dentro de uma caixa estilo input.
 * Clique no chip ou no botão × volta ao modo de busca com texto vazio —
 * evita o atrito de apagar manualmente o texto selecionado pra procurar
 * outro item.
 */
export function ItemTypeahead({
  opcoes,
  valor,
  onChange,
  ariaLabel,
  placeholder,
  mensagemVazio = 'Digite para buscar…',
  onConfirmarNovo,
}: Props) {
  const [texto, setTexto] = useState('');
  const [editando, setEditando] = useState(false);
  const [aberto, setAberto] = useState(false);
  const [posicao, setPosicao] = useState<Posicao | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listaRef = useRef<HTMLUListElement>(null);

  const mostrarChip = valor !== null && !editando;

  function recalcularPosicao() {
    // Usa o wrapper como âncora — ele existe em ambos os modos (chip e input).
    const el = wrapperRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const espacoAbaixo = window.innerHeight - rect.bottom;
    const alturaEstimada = 280;
    const abrirParaCima = espacoAbaixo < alturaEstimada && rect.top > espacoAbaixo;
    setPosicao({
      top: abrirParaCima ? rect.top - 4 : rect.bottom + 4,
      left: rect.left,
      width: rect.width,
      abrirParaCima,
    });
  }

  useLayoutEffect(() => {
    if (!aberto) return;
    recalcularPosicao();
    const handler = () => recalcularPosicao();
    window.addEventListener('scroll', handler, true);
    window.addEventListener('resize', handler);
    return () => {
      window.removeEventListener('scroll', handler, true);
      window.removeEventListener('resize', handler);
    };
  }, [aberto, texto]);

  useEffect(() => {
    if (!aberto) return;
    function handleClickFora(e: MouseEvent) {
      const alvo = e.target as Node;
      if (wrapperRef.current?.contains(alvo)) return;
      if (listaRef.current?.contains(alvo)) return;
      setAberto(false);
      setEditando(false);
    }
    document.addEventListener('mousedown', handleClickFora);
    return () => document.removeEventListener('mousedown', handleClickFora);
  }, [aberto]);

  const filtradas = useMemo(() => {
    const q = normalizar(texto.trim());
    if (q === '') return opcoes;
    return opcoes.filter((o) => {
      const buscaNorm = normalizar(`${o.busca} ${o.rotulo}`);
      return buscaNorm.includes(q);
    });
  }, [opcoes, texto]);

  // Aplica o cap só na renderização — `filtradas` (lista completa) ainda
  // é consultada pra "Enter seleciona se há só 1 resultado" e pra contar
  // quantos itens estão escondidos.
  const visiveis = useMemo(
    () => filtradas.slice(0, TAMANHO_MAX_VISIVEL),
    [filtradas],
  );
  const escondidas = filtradas.length - visiveis.length;

  const correspondeExatamente = filtradas.some(
    (o) => normalizar(o.rotulo) === normalizar(texto.trim()),
  );

  function selecionar(opcao: OpcaoTypeahead) {
    if (opcao.desabilitada) return;
    onChange({ tipo: 'existente', opcao });
    setTexto('');
    setAberto(false);
    setEditando(false);
  }

  function confirmarComoNovo() {
    const desc = texto.trim();
    if (desc === '') return;
    onChange({ tipo: 'novo', descricao: desc });
    void onConfirmarNovo?.(desc);
    setTexto('');
    setAberto(false);
    setEditando(false);
  }

  function limparSelecao() {
    onChange(null);
    setTexto('');
    setEditando(true);
    setAberto(true);
    // Foca o input no próximo tick — o input só existe quando !mostrarChip.
    window.setTimeout(() => inputRef.current?.focus(), 0);
  }

  function entrarEmEdicao() {
    setTexto('');
    setEditando(true);
    setAberto(true);
    window.setTimeout(() => inputRef.current?.focus(), 0);
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const v = e.target.value;
    setTexto(v);
    setAberto(true);
    const exato = opcoes.find((o) => normalizar(o.rotulo) === normalizar(v.trim()));
    if (exato) {
      onChange(exato.desabilitada ? null : { tipo: 'existente', opcao: exato });
    } else if (v.trim() === '') {
      onChange(null);
    } else {
      onChange({ tipo: 'novo', descricao: v });
    }
  }

  const mostrarSugerirNovo = aberto && !correspondeExatamente && texto.trim() !== '';

  const estiloLista: React.CSSProperties | undefined = posicao
    ? {
        position: 'fixed',
        top: posicao.abrirParaCima ? undefined : posicao.top,
        bottom: posicao.abrirParaCima ? window.innerHeight - posicao.top : undefined,
        left: posicao.left,
        width: posicao.width,
        maxHeight: 280,
        overflowY: 'auto',
      }
    : undefined;

  // ChipView e InputView compartilham o mesmo wrapper pro dropdown ancorar
  // sempre na mesma referência (inputRef). O input é renderizado em ambos
  // os modos, oculto quando há chip — assim o ref fica estável.
  return (
    <div className="typeahead" ref={wrapperRef}>
      {mostrarChip && valor ? (
        <button
          type="button"
          className="typeahead__caixa-chip input"
          onClick={entrarEmEdicao}
          onKeyDown={(e) => {
            if (e.key === 'Backspace' || e.key === 'Delete') {
              e.preventDefault();
              limparSelecao();
            } else if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              entrarEmEdicao();
            }
          }}
          aria-label={`${ariaLabel}: ${formatarValor(valor)}. Pressione Enter para editar, Delete para limpar.`}
        >
          <span className="typeahead__chip">{formatarValor(valor)}</span>
          <span
            className="typeahead__limpar"
            role="button"
            aria-label="Limpar seleção"
            data-tooltip-id="icon-tip"
            tabIndex={-1}
            onClick={(e) => {
              e.stopPropagation();
              limparSelecao();
            }}
          >
            ×
          </span>
        </button>
      ) : (
        <input
          ref={inputRef}
          className="input"
          value={texto}
          onChange={handleChange}
          onFocus={() => {
            setEditando(true);
            setAberto(true);
          }}
          onBlur={() => {
            window.setTimeout(() => {
              const ativo = document.activeElement;
              if (ativo && listaRef.current?.contains(ativo)) return;
              if (mostrarSugerirNovo) confirmarComoNovo();
              setEditando(false);
              // Se o usuário apagou tudo e havia valor selecionado, mantém limpo.
              // Se digitou algo mas não confirmou nem casou exato, deixa como tipo:novo
              // (handleChange já cuidou disso).
            }, 0);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              if (filtradas.length === 1 && !filtradas[0].desabilitada) selecionar(filtradas[0]);
              else if (mostrarSugerirNovo) confirmarComoNovo();
            } else if (e.key === 'Escape') {
              setAberto(false);
              setEditando(false);
            }
          }}
          aria-label={ariaLabel}
          placeholder={placeholder}
          autoComplete="off"
        />
      )}
      {aberto &&
        posicao &&
        createPortal(
          <ul
            ref={listaRef}
            className="typeahead__lista"
            role="listbox"
            style={estiloLista}
          >
            {visiveis.map((o) => (
              <li key={o.id}>
                <button
                  type="button"
                  className={`typeahead__item${o.desabilitada ? ' typeahead__item--desabilitada' : ''}`}
                  onClick={() => selecionar(o)}
                  role="option"
                  aria-selected={valor?.tipo === 'existente' && valor.opcao.id === o.id}
                  aria-disabled={o.desabilitada || undefined}
                  disabled={o.desabilitada}
                >
                  <span>{o.rotulo}</span>
                  {o.motivoDesabilitado && (
                    <span className="typeahead__meta typeahead__meta--bloqueio">
                      {o.motivoDesabilitado}
                    </span>
                  )}
                </button>
              </li>
            ))}
            {escondidas > 0 && (
              <li className="typeahead__vazio" aria-live="polite">
                +{escondidas.toLocaleString('pt-BR')} {escondidas === 1 ? 'item' : 'itens'} —
                digite código ou nome para refinar a busca.
              </li>
            )}
            {mostrarSugerirNovo && (
              <li>
                <button
                  type="button"
                  className="typeahead__item typeahead__item--novo"
                  onClick={confirmarComoNovo}
                >
                  + Cadastrar novo: <strong>{texto.trim()}</strong>
                </button>
              </li>
            )}
            {filtradas.length === 0 && !mostrarSugerirNovo && (
              <li className="typeahead__vazio">{mensagemVazio}</li>
            )}
          </ul>,
          document.body,
        )}
    </div>
  );
}

function formatarValor(v: ItemTypeaheadValor | null): string {
  if (!v) return '';
  if (v.tipo === 'existente') return v.opcao.rotulo;
  return v.descricao;
}
